import { queryOptions, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useIntegrationRollout } from 'v2/shared/hooks/use-integration-rollout'
import {
  createIntegrationSourceGateway,
  type IntegrationSourceGateway,
} from 'v2/shared/services/integration-source-gateway'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { externalAppsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { IntegrationSource, RExternalApp } from 'v2/types/api/settings'
import { errorToast } from 'v2/utils/toasts'
import { loadIntegrationConnections } from './utils/connection-rollout'

interface IntegrationConnectionsQueryOptions {
  isActive?: boolean
  isResolved?: boolean
  rolloutEnabled?: boolean
  gateway?: IntegrationSourceGateway
}

export const createIntegrationConnectionsQueryOptions = ({
  isActive,
  isResolved = true,
  rolloutEnabled = false,
  gateway = createIntegrationSourceGateway({ aciEnabled: rolloutEnabled }),
}: IntegrationConnectionsQueryOptions = {}) => {
  const mode: IntegrationSource = rolloutEnabled ? 'aci' : 'nango'
  return queryOptions({
    queryKey: externalAppsQueryKeys().externalOrgAppsV3(isActive, mode),
    // Wait for the rollout to resolve before reading either source, so we never
    // fire the legacy request for an org that is about to resolve to ACI. The ACI
    // service is not on the embed allow-list, so ACI orgs never read it in embed.
    enabled: isResolved && (!isEmbedMode() || !rolloutEnabled),
    queryFn: () =>
      loadIntegrationConnections({
        rolloutEnabled,
        fetchAci: async () => {
          const response = await gateway.getAciConnections()
          return isActive
            ? response.data.filter((connection) => connection.state === 'ACTIVE')
            : response.data
        },
        fetchLegacy: async () => {
          const response = await gateway.getLegacyConnections(isActive)
          return response.data
        },
      }),
  })
}

export const useExternalAppsQuery = () => {
  const { gateway, isResolved, mode } = useIntegrationRollout()
  return useQuery<RExternalApp>({
    queryKey: externalAppsQueryKeys().externalApps(mode),
    queryFn: async () => {
      const response = await gateway.getCatalog()
      return response.data
    },
    enabled: isResolved,
  })
}

export const useExternalAppsV2Query = () => {
  const { gateway, isResolved, mode } = useIntegrationRollout()
  return useQuery<RExternalApp>({
    queryKey: externalAppsQueryKeys().externalAppsV2(mode),
    queryFn: async () => {
      const response = await gateway.getCatalog()
      return response.data
    },
    enabled: isResolved,
  })
}

export const useExternalOrgAppsQuery = (isActive?: boolean, enabled = true) => {
  const { gateway, isResolved, useAciIntegrations } = useIntegrationRollout()
  const options = createIntegrationConnectionsQueryOptions({
    gateway,
    isActive,
    isResolved,
    rolloutEnabled: useAciIntegrations,
  })
  const query = useQuery({ ...options, enabled: options.enabled && enabled })
  return {
    ...query,
    // A disabled query reports isLoading:false, so "still waiting for the flag to
    // resolve" would read to callers as "loaded, and there is nothing" — the
    // integrations table and the contact-view config both flash their full-screen
    // empty state before the real data arrives. Unresolved is loading.
    isLoading: query.isLoading || !isResolved,
    data: query.data?.connections,
  }
}

export const useOAuthProvidersQuery = (enabled = true) => {
  const { gateway, isResolved, mode } = useIntegrationRollout()
  const query = useQuery({
    queryKey: externalAppsQueryKeys().oauthProviders(mode),
    queryFn: async () => (await gateway.getOAuthProviders()).data ?? [],
    enabled: enabled && isResolved,
  })

  useEffect(() => {
    if (query.isError) {
      errorToast({ title: 'Error', description: 'Failed to load OAuth providers' })
    }
  }, [query.isError])

  return query
}

export const useCustomFieldsQuery = (
  appId: string,
  source: IntegrationSource | undefined,
  isCustomFieldsEnabled: boolean,
  entities: string[],
  enabled: boolean,
) => {
  const { gateway, isResolved } = useIntegrationRollout()
  return useQuery({
    queryKey: externalAppsQueryKeys().customFields(appId, source),
    enabled: isResolved && isCustomFieldsEnabled && !!appId && enabled && entities.length > 0,
    queryFn: async () => {
      const response = await gateway.getCustomFields(source, appId, entities)
      return response.data?.fields_by_entity || {}
    },
  })
}

export const useAccessRecordsQuery = (
  orgAppUuid: string,
  source: IntegrationSource | undefined,
  enabled = true,
) => {
  const { gateway, isResolved } = useIntegrationRollout()
  return useQuery({
    queryKey: externalAppsQueryKeys().accessRecords(orgAppUuid, source),
    queryFn: async () => (await gateway.getAccessRecords(source, orgAppUuid)).data,
    enabled: Boolean(isResolved && orgAppUuid && enabled),
  })
}

export const useSalesforceAccountNameQuery = (
  salesforceContactId: string,
  salesforceAppId?: string,
) => {
  const { gateway, isResolved, mode, useAciIntegrations } = useIntegrationRollout()
  return useQuery({
    queryKey: externalAppsQueryKeys().salesforceAccount(salesforceContactId, salesforceAppId, mode),
    queryFn: async () => {
      if (!salesforceContactId || !salesforceAppId) return '-'
      const response = await gateway.executeManualAction({
        action_name: 'GET_CONTACT_BY_ID',
        input: { id: salesforceContactId },
        organization_external_app_uuid: salesforceAppId,
      })
      return (response.data.output as { account_name: string } | null)?.account_name || '-'
    },
    enabled: Boolean(isResolved && salesforceContactId && salesforceAppId && !useAciIntegrations),
  })
}
